'use client';

import {
	BN,
	BigNum,
	FOUR,
	MarketType,
	PublicKey,
	SpotBalanceType,
	SpotMarketConfig,
	SwapClientType,
	SwapMode,
	SwapProviderRoute,
	SwapQuote,
	SwapRouteFields,
	convertToNumber,
} from '@velocity-exchange/sdk';
import { MarketId, matchEnum } from '@velocity-exchange/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import useAccountSpotBalances from '../useAccountBankBalances';
import useCurrentSettings from '../useCurrentSettings';
import { useSwap } from '../velocityActions/swaps/useSwap';
import useVelocityClientIsReady from '../useVelocityClientIsReady';
import useAppEventEmitter from '../useAppEventEmitter';
import { getAccountsInUse } from 'src/utils/position';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';
import {
	getQuoteSwapClient,
	getSimulatedQuoteValues,
	getSwapToastId,
} from 'src/utils/swap';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { useExceedsMaxSpotBalance } from '../useMaxSpotBalance';
import useGetOraclePriceForMarket from '../useGetOraclePriceForMarket';
import { useDebouncedCallback } from 'use-debounce';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useModal from 'src/components/Modals/hooks/useModal';
import { useMarginInfoByUserKey } from '../query/accountTradingData/useMarginInfo';
import useIsAccountMarginEnabled from 'src/components/TradeForm/hooks/useIsAccountMarginEnabled';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import {
	DEFAULT_SWAP_MAX_ACCOUNTS,
	MIN_SWAP_MAX_ACCOUNTS,
} from 'src/constants/swap';
import { useEffectiveSwapClient } from '../velocityActions/swaps/useEffectiveSwapClient';

const MAX_ACCOUNTS_SWAP_BUFFER = Env.maxAccountsSwapBuffer;

const EMPTY_ROUTE_FIELS: SwapRouteFields = {
	inputMint: '',
	outputMint: '',
	inAmount: '0',
	outAmount: '0',
	swapMode: 'ExactIn',
	slippageBps: 0,
};

/**
 * Route stand-in for a quote that failed before a provider was asked, so the
 * error can be surfaced through the normal quote shape. Carries no route, and
 * the errored quote it belongs to never reaches execution.
 */
const emptyProviderRoute = (provider: SwapClientType): SwapProviderRoute =>
	provider === 'titan'
		? { provider: 'titan', route: null, routed: EMPTY_ROUTE_FIELS }
		: { provider: 'jupiter', quote: null, routed: EMPTY_ROUTE_FIELS };

export default function useSwapFormEngine(
	swapFromMarket: SpotMarketConfig,
	swapToMarket: SpotMarketConfig,
	defaultSlippageTolerancePct: number,
	defaultSwapMode: SwapMode = 'ExactIn',
	targetAccountKey?: string
) {
	const { swap } = useSwap();
	const slippageTolerance = useVelocityStore(
		(s) => s.tradeForm.slippageTolerance
	);
	// Not the configured client when the destination forces a provider — the
	// price shown has to come from whoever executes the swap.
	const swapClient = useEffectiveSwapClient(swapToMarket.mint);
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const connection = useVelocityStore((s) => s.connection.current);

	const [savedSettings, setSavedSettings] = useCurrentSettings();
	const { show } = useModal();

	const [isMarginCheckboxChecked, setIsMarginCheckboxChecked] =
		useSyncLocalStorage('swapFormMarginCheckboxChecked', false);
	const isAccountMarginEnabled = useIsAccountMarginEnabled();
	const isFormMarginEnabled = isAccountMarginEnabled && isMarginCheckboxChecked;

	const toggleIsMarginCheckboxChecked = useCallback(() => {
		setIsMarginCheckboxChecked((curr) => !curr);
	}, [setIsMarginCheckboxChecked]);

	const spotMarketPoolId = swapFromMarket.poolId;

	if (spotMarketPoolId !== swapToMarket.poolId) {
		throw new Error('Swap from and to markets must be in the same pool');
	}

	const currentUserKey = useVelocityAccountStore((s) => s.currentUserKey);
	const effectiveUserKey = targetAccountKey ?? currentUserKey ?? '';
	const targetAccount = useVelocityAccountStore(
		(s) => s.accounts[effectiveUserKey]
	);
	const targetUserKey = targetAccount?.userKey;
	const { data: targetMarginInfo } = useMarginInfoByUserKey(
		effectiveUserKey || undefined
	);

	const accountBalances = useAccountSpotBalances(
		targetUserKey,
		undefined,
		spotMarketPoolId
	);
	const velocityClientIsReady = useVelocityClientIsReady();
	const isGeoblocked = useVelocityStore((s) => s.isGeoblocked);
	const setStore = useVelocityStore((s) => s.set);
	const appEventEmitter = useAppEventEmitter();
	const willExceedMaxSpotBalance = useExceedsMaxSpotBalance(
		swapToMarket.marketIndex
	);

	const [swapFromAmount, setSwapFromAmount] = useState<string>('');
	const [swapToAmount, setSwapToAmount] = useState<string>('');
	const [quoteDetails, setQuoteDetails] = useState<SwapQuote | null>(null);
	const [swapPrice, setSwapPrice] = useState(0);
	const [swapMode, setSwapMode] = useState(defaultSwapMode);
	const [isLoading, setIsLoading] = useState(false);
	const [isApprovingTxn, setIsApprovingTxn] = useState(false); // this is needed because it takes a couple of seconds for the wallet approval to show up, and we don't want the user to spam the swap button
	const [
		showPriceEstimateOracleDivergenceWarning,
		setShowPriceEstimateOracleDivergenceWarning,
	] = useState(false);
	const [priceDivergencePct, setPriceDivergencePct] = useState<string>();
	const [maxSwapAmountDetails, setMaxSwapAmountDetails] = useState<{
		inAmount: BN;
		outAmount: BN;
		leverage: BN;
	}>();
	const [additionalAccountsBuffer, setAdditionalAccountsBuffer] = useState(
		MAX_ACCOUNTS_SWAP_BUFFER
	);

	// Use oracle price instead of jup price data because sometimes jup price data feed is unavailable for the pair
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const fromTokenOraclePrice = getOraclePriceForMarket(
		new MarketId(swapFromMarket.marketIndex, MarketType.SPOT)
	);
	const toTokenOraclePrice = getOraclePriceForMarket(
		new MarketId(swapToMarket.marketIndex, MarketType.SPOT)
	);
	// The from token's price in to tokens. Never inverted, unlike the display
	// price `useJupiterTokenPairData` returns for a stablecoin-from pair.
	const tokenPairPrice =
		(fromTokenOraclePrice?.toNum() ?? 0) / (toTokenOraclePrice?.toNum() ?? 0);

	const swapFromMarketIndexRef = useRef(swapFromMarket.marketIndex);
	const swapToMarketIndexRef = useRef(swapToMarket.marketIndex);
	const isApprovingTxnRef = useRef(isApprovingTxn);
	// Budget the displayed quote was fetched with, so the tx gets built against
	// the same route constraint the user was quoted under.
	const quoteMaxAccountsRef = useRef<number | undefined>(undefined);

	const isExactIn = swapMode === 'ExactIn';
	const enableVersionedTx = savedSettings.enableVersionedTx;
	const directRouteSwaps = savedSettings.directRouteSwaps;

	// input variables
	const fromTokenBalanceAccount = accountBalances.find(
		(acc) => acc.asset.marketIndex === swapFromMarket.marketIndex
	);
	let fromTokenBalance =
		fromTokenBalanceAccount?.balance ??
		BigNum.zero(swapFromMarket.precisionExp);
	fromTokenBalance = matchEnum(
		SpotBalanceType.BORROW,
		fromTokenBalanceAccount?.balanceType
	)
		? fromTokenBalance.neg()
		: fromTokenBalance;

	const swapFromAmountBigNum = parseInputBigNumOrZero(
		swapFromAmount,
		swapFromMarket.precisionExp
	);

	const swapToAmountBigNum = parseInputBigNumOrZero(
		swapToAmount,
		swapToMarket.precisionExp
	);

	const desiredSwapAmount = isExactIn
		? swapFromAmountBigNum
		: swapToAmountBigNum;

	const desiredSwapAmountNum = desiredSwapAmount.toNum();

	const inputNumericAmount = isExactIn
		? swapFromAmountBigNum.toNum()
		: swapToAmountBigNum.toNum();

	// margin variables
	const maxLeverageAllowed = BigNum.from(
		maxSwapAmountDetails?.leverage.toString(),
		FOUR
	).toNum();
	const maxSwapSize = calcMaxSwapSize();
	const currentLeverage = targetMarginInfo?.leverage;

	const isInsufficientBalance =
		swapFromAmountBigNum.toNum() > maxSwapSize.toNum();

	const borrowAmount = swapFromAmountBigNum.sub(fromTokenBalance);

	const toTokenBalanceAccount = accountBalances.find(
		(acc) => acc.asset.marketIndex === swapToMarket.marketIndex
	);
	const willReduceBorrow = matchEnum(
		toTokenBalanceAccount?.balanceType,
		SpotBalanceType.BORROW
	);

	const borrowToBeRepaid =
		willReduceBorrow && toTokenBalanceAccount
			? BigNum.min(toTokenBalanceAccount.balance, swapToAmountBigNum)
			: BigNum.zero(toTokenBalanceAccount?.asset.precisionExp);

	// Logic to make sure user input choice matches quote
	// This should prevent users from accidentally swapping with a stale quote that doesn't match the current input
	let marketsMatchQuote = false;
	if (
		quoteDetails &&
		!quoteDetails.error &&
		swapFromMarket &&
		swapToMarket &&
		swapFromAmount &&
		swapToAmount
	) {
		const quoteDetailsInAmountNum = convertToNumber(
			new BN(quoteDetails.inAmount),
			swapFromMarket.precision
		);
		const quoteDetailsOutAmountNum = convertToNumber(
			new BN(quoteDetails.outAmount),
			swapToMarket.precision
		);

		const correctMints =
			quoteDetails.inputMint === swapFromMarket.mint.toString() &&
			quoteDetails.outputMint === swapToMarket.mint.toString();

		const correctMode = isExactIn
			? quoteDetails.swapMode === 'ExactIn'
			: quoteDetails.swapMode === 'ExactOut';

		const correctAmounts = isExactIn
			? parseFloat(swapFromAmount) === quoteDetailsInAmountNum
			: parseFloat(swapToAmount) === quoteDetailsOutAmountNum;

		marketsMatchQuote = correctMints && correctMode && correctAmounts;
	}

	const isAboveMaxSwapSizeWithoutLeverage =
		!isFormMarginEnabled && isInsufficientBalance;

	const isButtonDisabled =
		isLoading ||
		!marketsMatchQuote ||
		!quoteDetails ||
		!enableVersionedTx ||
		isApprovingTxn ||
		isInsufficientBalance ||
		isGeoblocked ||
		!!quoteDetails?.error ||
		willExceedMaxSpotBalance ||
		isAboveMaxSwapSizeWithoutLeverage;

	const [callFetchRoute, setCallFetchRoute] = useState(true);

	// short term fix to default swap form's slippage, otherwise will be using tradeform's slippage
	useEffect(() => {
		setStore((s) => {
			s.tradeForm.slippageTolerance = defaultSlippageTolerancePct;
		});
	}, [defaultSlippageTolerancePct]);

	// Fetching routes from Jupiter is expensive, so we only want to do it when necessary.
	// When we don't need real quotes (e.g. during slider movement), we use simulated amounts
	// to provide instant feedback to the user.
	useEffect(() => {
		if (callFetchRoute) {
			debouncedCallFetchRoute();
		} else {
			updateSimulatedSwapAmount();
		}
	}, [desiredSwapAmountNum, callFetchRoute]);

	useDebounce(
		() => {
			getRoute();
		},
		300,
		[
			swapFromMarket,
			swapToMarket,
			slippageTolerance,
			directRouteSwaps,
			isExactIn,
			additionalAccountsBuffer,
			// A quote only builds on the provider that issued it, so switching
			// providers has to re-quote rather than leave the old one on screen.
			swapClient,
		]
	);

	// listen for successful swap event
	useEffect(() => {
		const handler = (swapRecordEvent: any) => {
			// check if the successful swap event is for the currently transacting swap
			if (
				swapFromMarketIndexRef.current === swapRecordEvent.inMarketIndex &&
				swapToMarketIndexRef.current === swapRecordEvent.outMarketIndex &&
				isApprovingTxnRef.current
			) {
				setIsApprovingTxn(false);
				getRouteRef.current();
			}
		};

		appEventEmitter.on('successfulSwap', handler);

		return () => {
			appEventEmitter.off('successfulSwap', handler);
		};
	}, [appEventEmitter]);

	useEffect(() => {
		if (velocityClientIsReady && targetAccount?.client?.isSubscribed) {
			setMaxSwapAmountDetails(
				targetAccount?.client?.getMaxSwapAmount({
					inMarketIndex: swapFromMarket.marketIndex,
					outMarketIndex: swapToMarket.marketIndex,
				})
			);
		}
	}, [
		velocityClientIsReady,
		swapFromMarket.marketIndex,
		swapToMarket.marketIndex,
		!!targetAccount?.client,
		fromTokenBalance.toNum(),
	]);

	// refresh optimal route every 30s
	useEffect(() => {
		let interval: ReturnType<typeof setInterval>;
		if (quoteDetails) {
			interval = setInterval(() => getRoute(), 10000);
		}

		return () => clearInterval(interval);
	}, [quoteDetails]);

	// remove other swap amount when route is loading
	useEffect(() => {
		if (isLoading) {
			getSwapConfig(isExactIn).setOtherAmount('');
		}
	}, [isLoading, isExactIn]);

	// update other swap amount when route details change
	useDebounce(
		() => {
			const { otherMarket, setOtherAmount } = getSwapConfig(isExactIn);

			if (!quoteDetails) {
				setOtherAmount('');
				return;
			}

			const amountStr = BigNum.from(
				isExactIn ? quoteDetails.outAmount : quoteDetails.inAmount,
				otherMarket.precisionExp
			).printShort();

			setOtherAmount(amountStr);
		},
		300,
		[quoteDetails, swapFromMarket, swapToMarket, isExactIn]
	);

	// set default swap slippage to 0.1%
	useEffect(() => {
		setStore((s) => {
			s.tradeForm.slippageTolerance = 0.1;
		});
	}, []);

	function calcMaxSwapSize() {
		let maxLeveragedSwapSize = BigNum.from(
			maxSwapAmountDetails?.inAmount,
			swapFromMarket.precisionExp
		);

		// prevent race condition where either variable is not using the correct market yet
		if (!fromTokenBalance.precision.eq(maxLeveragedSwapSize.precision)) {
			return BigNum.zero();
		}

		if (isFormMarginEnabled) {
			// allow a 1% buffer if max size is leveraged, since numbers are constantly updated
			maxLeveragedSwapSize = maxLeveragedSwapSize
				.mul(new BN(99))
				.div(new BN(100));
		}

		let calculatedMaxSwapSize: BigNum;

		if (isFormMarginEnabled) {
			calculatedMaxSwapSize = maxLeveragedSwapSize;
		} else {
			if (fromTokenBalance.gt(maxLeveragedSwapSize)) {
				// max swap size should never be greater than the max leveraged size
				calculatedMaxSwapSize = maxLeveragedSwapSize;
			} else {
				calculatedMaxSwapSize = fromTokenBalance.isNeg()
					? BigNum.zero(swapFromMarket.precisionExp)
					: fromTokenBalance;
			}
		}

		return calculatedMaxSwapSize;
	}
	const getRoute = useCallback(async () => {
		setIsLoading(true);
		try {
			if (desiredSwapAmountNum > 0 && targetAccount?.client) {
				const accountsInUse = getAccountsInUse(targetAccount?.client);

				// An account with many open positions can subtract its way under the
				// floor, where neither provider routes — same clamp the retry ladder
				// applies, so the first attempt isn't spent on a guaranteed failure.
				const maxAccounts = Math.max(
					DEFAULT_SWAP_MAX_ACCOUNTS - additionalAccountsBuffer - accountsInUse,
					MIN_SWAP_MAX_ACCOUNTS
				);

				quoteMaxAccountsRef.current = maxAccounts;
				const usdEstimate =
					(fromTokenOraclePrice?.toNum() ?? 0) * swapFromAmountBigNum.toNum();

				const slippageParams =
					slippageTolerance === 'dynamic'
						? {
								autoSlippage: true,
								maxAutoSlippageBps: 500, // 5 pct
								usdEstimate: Math.round(usdEstimate),
							}
						: {
								slippageBps: slippageTolerance ? slippageTolerance * 100 : 500, // 5% slippage if user selects inifinite
								autoSlippage: false,
							};

				if (
					slippageParams.slippageBps !== undefined &&
					slippageParams.slippageBps < 1
				) {
					setQuoteDetails({
						inAmount: '0',
						outAmount: '0',
						inputMint: swapFromMarket.mint.toBase58(),
						outputMint: swapToMarket.mint.toBase58(),
						slippageBps: 1,
						otherAmountThreshold: '0',
						swapMode: 'ExactIn',
						errorCode: 'SLIPPAGE_TOO_LOW',
						error: 'Slippage is too low',
						priceImpactPct: '0',
						routePlan: [],
						providerRoute: emptyProviderRoute(
							swapClient?.getClientType() ?? 'jupiter'
						),
					});
					return;
				}

				// The v2 build endpoint prices this when it can; the route it
				// returns still executes on `swapClient`.
				const quoteClient = getQuoteSwapClient({
					swapClient,
					connection,
					swapMode,
					autoSlippage: slippageParams.autoSlippage,
					userPublicKey: authority,
				});

				const quote = await quoteClient?.getQuote({
					inputMint: swapFromMarket.mint,
					outputMint: swapToMarket.mint,
					amount: desiredSwapAmount.val,
					userPublicKey: authority as PublicKey,
					swapMode,
					maxAccounts,
					onlyDirectRoutes: directRouteSwaps,
					...slippageParams,
				});

				if (!quote) {
					console.error('Missing quote response');
					return;
				}

				// Calculate price impact if not provided in quote (e.g., for Titan quotes)
				let quoteWithPriceImpact = quote;
				if (
					!('priceImpactPct' in quote) &&
					fromTokenOraclePrice &&
					toTokenOraclePrice
				) {
					const inputUsdValue =
						convertToNumber(new BN(quote.inAmount), swapFromMarket.precision) *
						fromTokenOraclePrice.toNum();

					const outputUsdValue =
						convertToNumber(new BN(quote.outAmount), swapToMarket.precision) *
						toTokenOraclePrice.toNum();

					const usdDifference = inputUsdValue - outputUsdValue;
					const priceImpactPct =
						inputUsdValue > 0 ? usdDifference / inputUsdValue : 0;

					// Add the calculated price impact to the quote (as decimal, not percentage)
					quoteWithPriceImpact = {
						...quote,
						priceImpactPct: priceImpactPct.toString(),
					};
				}

				setQuoteDetails(quoteWithPriceImpact);

				if (quote.error) {
					console.error('Error in quote response', quote);
					if (
						quote.errorCode === 'ROUTE_PLAN_DOES_NOT_CONSUME_ALL_THE_AMOUNT'
					) {
						// Try a low number for the max accounts buffer if we hit this error
						if (additionalAccountsBuffer > 5) {
							setAdditionalAccountsBuffer(5);
						}
					}
				}

				const quoteSwapPrice =
					(+quote.outAmount / +quote.inAmount) *
					10 **
						(swapFromMarket.precisionExp.toNumber() -
							swapToMarket.precisionExp.toNumber());

				const priceDivergence = Math.abs(quoteSwapPrice - tokenPairPrice);

				const quotePriceDivergencePct =
					(priceDivergence / tokenPairPrice) * 100;

				setShowPriceEstimateOracleDivergenceWarning(
					quotePriceDivergencePct > Env.priceDivergenceWarningThreshold
				);
				setPriceDivergencePct(quotePriceDivergencePct.toFixed(2));
				setSwapPrice(quoteSwapPrice);
			} else {
				setQuoteDetails(null);
				setSwapPrice(0);
			}
		} catch (err) {
			console.error('Error fetching jupiter routes', err);
			setQuoteDetails(null);
			setSwapPrice(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		desiredSwapAmountNum,
		targetAccount?.client,
		additionalAccountsBuffer,
		fromTokenOraclePrice,
		swapFromAmountBigNum,
		slippageTolerance,
		swapClient,
		connection,
		// A v2 quote is bound to the wallet it was taken for, so a wallet switch
		// has to re-quote rather than leave one that execution will reject.
		authority,
		swapFromMarket.mint,
		swapFromMarket.precisionExp,
		swapFromMarket.marketIndex,
		swapToMarket.mint,
		swapToMarket.precisionExp,
		swapToMarket.marketIndex,
		desiredSwapAmount.val,
		swapMode,
		directRouteSwaps,
		tokenPairPrice,
	]);

	const getRouteRef = useRef(getRoute);

	// sync swap market indexes with their refs
	useEffect(() => {
		swapFromMarketIndexRef.current = swapFromMarket.marketIndex;
		swapToMarketIndexRef.current = swapToMarket.marketIndex;
		isApprovingTxnRef.current = isApprovingTxn;
		getRouteRef.current = getRoute;
	}, [
		swapFromMarket.marketIndex,
		swapToMarket.marketIndex,
		isApprovingTxn,
		getRoute,
	]);

	const debouncedCallFetchRoute = useDebouncedCallback(getRoute, 300);

	function updateSimulatedSwapAmount() {
		if (!isFinite(tokenPairPrice) || tokenPairPrice <= 0) {
			if (swapMode === 'ExactIn') {
				setSwapToAmount('0');
			} else {
				setSwapFromAmount('0');
			}
			return;
		}

		if (!fromTokenOraclePrice || !toTokenOraclePrice) {
			// we shouldn't update swap details if for some reason oracle prices are missing
			return;
		}

		const { swapInAmount, swapOutAmount } = getSimulatedQuoteValues(
			desiredSwapAmount,
			swapMode,
			fromTokenOraclePrice,
			toTokenOraclePrice
		);

		if (swapMode === 'ExactIn') {
			setSwapToAmount(swapOutAmount.toNum().toString());
		} else {
			setSwapFromAmount(swapInAmount.toNum().toString());
		}

		setSwapPrice(tokenPairPrice);
	}

	const handleSetSwapFromAmount = (amount: string, callRouteFetch = true) => {
		setCallFetchRoute(callRouteFetch);
		setSwapFromAmount(amount);
	};

	const handleSetSwapToAmount = (amount: string, callRouteFetch = true) => {
		setCallFetchRoute(callRouteFetch);
		setSwapToAmount(amount);
	};

	function getSwapConfig(exactIn: boolean) {
		if (exactIn) {
			return {
				exactMarket: swapFromMarket,
				otherMarket: swapToMarket,
				exactAmount: swapFromAmount,
				setExactAmount: handleSetSwapFromAmount,
				setOtherAmount: handleSetSwapToAmount,
			};
		} else {
			return {
				exactMarket: swapToMarket,
				otherMarket: swapFromMarket,
				exactAmount: swapToAmount,
				setExactAmount: handleSetSwapToAmount,
				setOtherAmount: handleSetSwapFromAmount,
			};
		}
	}

	// If max size changes, check if the from amount needs to reduced.
	const swapFromAmountBigNumRef = useSyncedRef(swapFromAmountBigNum);
	useEffect(() => {
		if (swapFromAmountBigNumRef.current.gt(maxSwapSize)) {
			handleSetSwapFromAmount(maxSwapSize.print());
		}
	}, [maxSwapSize.toNum()]);

	const resetForm = () => {
		setSwapFromAmount('');
		setSwapToAmount('');
		setQuoteDetails(null);
		setSwapPrice(0);
	};

	const enableVersionedTransaction = () => {
		setSavedSettings({
			...savedSettings,
			enableVersionedTx: true,
		});
		notify({
			type: 'success',
			message: 'Versioned transaction enabled',
			description: 'You can now use swaps.',
		});
	};

	type SwapResult = 'success' | 'fail' | 'deferred';

	const handleSwap = async (): Promise<SwapResult> => {
		if (isInsufficientBalance) {
			notify({
				type: 'error',
				description: 'You cannot swap more than your balance',
				message: 'Swap failed',
			});

			return 'fail';
		}

		if (!quoteDetails) {
			console.error('Route details not provided for swap');
			return 'fail';
		}

		// hardcode 2 seconds disable after submit
		setIsApprovingTxn(true);
		setTimeout(() => {
			setIsApprovingTxn(false);
		}, 3000);

		try {
			if (savedSettings.showTradeConfirmation) {
				// lock in swap details to show in the modal
				setStore((s) => {
					s.swap = {
						fromMarketIndex: swapFromMarket.marketIndex,
						toMarketIndex: swapToMarket.marketIndex,
						poolId: swapFromMarket.poolId,
						swapDetails: {
							userKey: targetUserKey ?? '',
							amount: desiredSwapAmount.val,
							fromMarketIndex: swapFromMarket.marketIndex,
							toMarketIndex: swapToMarket.marketIndex,
							swapMode,
							quote: quoteDetails,
							onlyDirectRoutes: directRouteSwaps,
							slippageBps: quoteDetails?.slippageBps,
							maxAccounts: quoteMaxAccountsRef.current,
						},
					};
				});
				show({ type: 'swap_confirmation', props: {} });

				return 'deferred';
			}

			const swapToastId = getSwapToastId(
				swapFromMarket.marketIndex,
				swapToMarket.marketIndex
			);
			const result = await swap(swapToastId, {
				userKey: targetUserKey ?? '',
				amount: desiredSwapAmount.val,
				fromMarketIndex: swapFromMarket.marketIndex,
				toMarketIndex: swapToMarket.marketIndex,
				swapMode,
				quote: quoteDetails,
				onlyDirectRoutes: directRouteSwaps,
				slippageBps: quoteDetails?.slippageBps,
				maxAccounts: quoteMaxAccountsRef.current,
			});

			if (result) {
				return 'success';
			}

			return 'fail';
		} catch (err) {
			console.error('Error swapping', err);
			return 'fail';
		} finally {
			getRoute();
		}
	};

	const calcOtherAmountAtMaxSlippage = () => {
		const otherAmount = +(
			(isExactIn ? quoteDetails?.outAmount : quoteDetails?.inAmount) ?? '0'
		);

		const slippagePreference =
			slippageTolerance === 'dynamic' ? 0.1 : slippageTolerance;

		const slippageBps = quoteDetails?.slippageBps ?? slippagePreference;

		const slippageToApply = isExactIn
			? 1 - slippageBps / 1000
			: 1 + slippageBps / 1000;

		const otherAmountAtMaxSlippage = (
			(otherAmount *
				(isNaN(slippageToApply) ? slippagePreference : slippageToApply)) /
			(getSwapConfig(isExactIn).otherMarket.precision?.toNumber() ?? 1)
		).toLocaleString(Env.locale, {
			maximumFractionDigits:
				getSwapConfig(isExactIn).otherMarket.precisionExp?.toNumber() ?? 0,
			minimumFractionDigits:
				getSwapConfig(isExactIn).otherMarket.precisionExp?.toNumber() ?? 0,
		});

		return Number(otherAmountAtMaxSlippage) > 0
			? otherAmountAtMaxSlippage
			: '-';
	};

	return {
		enableVersionedTx,
		enableVersionedTransaction,
		swapMode,
		setSwapMode,
		swapFromAmount,
		setSwapFromAmount: handleSetSwapFromAmount,
		swapToAmount,
		setSwapToAmount: handleSetSwapToAmount,
		inputNumericAmount,
		tokenPairPrice,
		getSwapConfig,
		quoteDetails,
		swapPrice,
		resetForm,
		handleSwap,
		isLoading,
		setIsLoading,
		isButtonDisabled,
		fromTokenBalance,
		maxLeverageAllowed,
		maxSwapSize,
		currentLeverage,
		isInsufficientBalance,
		calcOtherAmountAtMaxSlippage,
		isGeoblocked,
		borrowAmount,
		borrowToBeRepaid,
		isApprovingTxn,
		hasMaxSpotBalance: willExceedMaxSpotBalance,
		showPriceEstimateOracleDivergenceWarning,
		priceDivergencePct,
		isAboveMaxSwapSizeWithoutLeverage,
		isAccountMarginEnabled,
		isMarginCheckboxChecked,
		toggleIsMarginCheckboxChecked,
	};
}
